<template>
  <main class="market-report">
    <header class="report-hero">
      <p>Daily Market Report</p>
      <h1>每日市场报告</h1>
      <span>期权监控 · 大宗商品 · 货币汇率 · 新闻联播热点</span>
    </header>

    <section class="report-shell">
      <div class="control-panel">
        <div class="control-main">
          <label>
            <span>报告日期</span>
            <input
              v-model.trim="queryDate"
              placeholder="YYYY-MM-DD"
              list="market-report-dates"
              @keyup.enter="loadReport"
            >
            <datalist id="market-report-dates">
              <option v-for="item in reportDates" :key="item.date" :value="item.date">
                {{ item.date }} · {{ statusLabel(item.status) }}
              </option>
            </datalist>
          </label>

          <label>
            <span>最近报告</span>
            <select v-model="queryDate" @change="loadReport">
              <option value="">最新报告</option>
              <option v-for="item in reportDates" :key="item.date" :value="item.date">
                {{ item.date }} · {{ statusLabel(item.status) }}
              </option>
            </select>
          </label>
        </div>

        <div class="control-actions">
          <button type="button" class="secondary" @click="loadLatestReport">最新</button>
          <button type="button" @click="loadReport">查询</button>
        </div>

        <div class="report-status-card" v-if="report">
          <span>当前报告</span>
          <strong>{{ report.report_date }}</strong>
          <em :class="'status-' + report.status">{{ statusLabel(report.status) }}</em>
          <small v-if="report.channel">通道 {{ report.channel }}</small>
        </div>
      </div>

      <div v-if="loading" class="state-message">报告加载中...</div>
      <div v-if="error" class="state-message error-msg">{{ error }}</div>
      <div v-if="fxError" class="state-message error-msg">{{ fxError }}</div>

      <section class="buffett-panel" v-if="buffett">
        <div class="panel-heading">
          <div>
            <p>Buffett Indicator</p>
            <h2>A股证券化率</h2>
          </div>
          <button type="button" class="secondary" @click="refreshBuffett" :disabled="buffettLoading">
            {{ buffettLoading ? '刷新中...' : '刷新' }}
          </button>
        </div>
        <div class="buffett-grid">
          <div class="ratio-block" :class="'verdict-' + buffett.verdict">
            <strong>{{ buffett.ratio_pct.toFixed(1) }}%</strong>
            <span>{{ buffett.detail && buffett.detail.band }}</span>
          </div>
          <div>
            <span>总市值</span>
            <strong>{{ (buffett.total_mktcap_100m / 10000).toFixed(2) }} 万亿元</strong>
          </div>
          <div>
            <span>上年GDP</span>
            <strong>{{ (buffett.gdp_100m / 10000).toFixed(2) }} 万亿元</strong>
          </div>
          <div>
            <span>统计范围</span>
            <strong>{{ buffett.stock_count }} 只</strong>
          </div>
        </div>
        <div class="buffett-footer">
          <span>
            采集于 {{ buffett.captured_at }}
            <template v-if="buffettJustRefreshed"> · 已刷新</template>
            <template v-if="buffett.estimated"> · 临时推演值</template>
          </span>
          <span>
            <router-link :to="{ name: 'a-share-market-cap-gdp' }">查看每日走势</router-link>
            ·
            <a @click="showBuffettDetail = !showBuffettDetail">
              {{ showBuffettDetail ? '收起评估过程' : '查看评估过程' }}
            </a>
          </span>
        </div>
        <div class="buffett-note" v-if="buffett.estimated">
          深交所官方接口当前不可达，本值以上一交易所官方值为锚点按备用序列日变动率推演，官方恢复后自动覆盖。
        </div>
        <div class="buffett-note" v-if="buffettError">{{ buffettError }}</div>
        <ol class="eval-steps" v-if="showBuffettDetail && buffett.detail && buffett.detail.steps">
          <li v-for="(s, si) in buffett.detail.steps" :key="si">{{ s }}</li>
        </ol>
      </section>

      <section class="fx-panel" v-if="fxSummary">
        <div class="panel-heading">
          <div>
            <p>Key FX Rates</p>
            <h2>重点汇率</h2>
          </div>
          <span class="fx-as-of">截至 {{ fxSummary.as_of_date }}</span>
        </div>
        <div class="fx-rate-grid">
          <button
            type="button"
            :class="['fx-rate-card', { active: activeFxKey === 'usd_cny' }]"
            :aria-pressed="activeFxKey === 'usd_cny'"
            v-if="fxSummary.usd_cny"
            @click="activeFxKey = 'usd_cny'"
          >
            <span>美元 / 人民币</span>
            <strong>{{ fxSummary.usd_cny.rate.toFixed(4) }}</strong>
            <small>{{ fxSummary.usd_cny.unit_text }}</small>
            <em>{{ fxSummary.usd_cny.quote_date }}</em>
          </button>
          <button
            type="button"
            :class="['fx-rate-card', { active: activeFxKey === 'cny_jpy' }]"
            :aria-pressed="activeFxKey === 'cny_jpy'"
            v-if="fxSummary.cny_jpy"
            @click="activeFxKey = 'cny_jpy'"
          >
            <span>人民币 / 日元</span>
            <strong>{{ fxSummary.cny_jpy.rate.toFixed(4) }}</strong>
            <small>{{ fxSummary.cny_jpy.unit_text }}</small>
            <em>{{ fxSummary.cny_jpy.quote_date }} · 交叉汇率</em>
          </button>
          <button
            type="button"
            :class="['fx-rate-card', { active: activeFxKey === 'usd_jpy' }]"
            :aria-pressed="activeFxKey === 'usd_jpy'"
            v-if="fxSummary.usd_jpy"
            @click="activeFxKey = 'usd_jpy'"
          >
            <span>美元 / 日元</span>
            <strong>{{ fxSummary.usd_jpy.rate.toFixed(4) }}</strong>
            <small>{{ fxSummary.usd_jpy.unit_text }}</small>
            <em>{{ fxSummary.usd_jpy.quote_date }}</em>
          </button>
        </div>
        <p class="fx-switch-hint">当前：{{ activeFxLabel }} · 点击上方卡片切换历史对比</p>
        <div class="fx-comparison" v-if="activeFxComparisons.length">
          <div
            v-for="item in activeFxComparisons"
            :key="item.year"
            :class="['fx-change-card', fxDirectionClass(item)]"
          >
            <span>较 {{ item.year }} 年末</span>
            <strong>{{ signedPct(item.change_pct) }}</strong>
            <small>{{ item.direction }}</small>
            <em v-if="item.baseline_rate != null">基准 {{ item.baseline_rate.toFixed(4) }}</em>
          </div>
        </div>
        <p class="fx-note">{{ activeFxNote }}</p>
      </section>

      <section class="report-card" v-if="report">
        <div class="panel-heading">
          <div>
            <p>Report Content</p>
            <h2>{{ report.report_date }} 市场报告</h2>
          </div>
        </div>
        <pre class="report-body">{{ report.content_md }}</pre>
      </section>

      <section class="valuation-panel" v-if="valuations.length > 0">
        <div class="panel-heading">
          <div>
            <p>Valuation Overview</p>
            <h2>估值总览</h2>
          </div>
          <span class="model-name" v-if="modelName">模型 {{ modelName }}</span>
        </div>
        <p class="model-hint">点击行查看该标的的完整模型评估过程。</p>
        <div class="table-wrapper">
          <table class="valuation-table">
            <thead>
              <tr>
                <th>标的</th><th>名称</th><th>类别</th>
                <th>1年Z</th><th>5年Z</th><th>52周分位</th><th>比价Z</th><th>综合分</th><th>结论</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="v in valuations">
                <tr :key="v.symbol" class="valuation-row" @click="toggleExpand(v.symbol)">
                  <td class="ticker-cell">{{ v.symbol }}</td>
                  <td>{{ v.name }}</td>
                  <td>{{ categoryLabel(v.category) }}</td>
                  <td>{{ fmt(v.z_score_1y) }}</td>
                  <td>{{ fmt(v.z_score_5y) }}</td>
                  <td>{{ v.percentile_52w != null ? v.percentile_52w.toFixed(0) + '%' : '-' }}</td>
                  <td>{{ fmt(v.ratio_z_score) }}</td>
                  <td :class="compositeClass(v)">{{ compositeOf(v) }}</td>
                  <td>
                    <span :class="'verdict-' + v.verdict">{{ verdictLabel(v.verdict) }}</span>
                    <span v-if="v.detail && v.detail.confidence && v.detail.confidence !== '无'" class="confidence">
                      置信{{ v.detail.confidence }}
                    </span>
                  </td>
                </tr>
                <tr v-if="expandedSymbol === v.symbol && v.detail" :key="v.symbol + '-detail'" class="detail-row">
                  <td colspan="9">
                    <div class="eval-process">
                      <h4>模型评估过程 - {{ v.name }} ({{ v.symbol }})</h4>
                      <table class="factor-table" v-if="v.detail.factors">
                        <thead>
                          <tr><th>因子</th><th>原始值</th><th>得分</th><th>权重</th><th>贡献</th></tr>
                        </thead>
                        <tbody>
                          <tr v-for="f in v.detail.factors" :key="f.key">
                            <td>{{ f.label }}</td>
                            <td>{{ f.value != null ? f.value : '缺失' }}</td>
                            <td>{{ f.score != null ? f.score.toFixed(2) : '-' }}</td>
                            <td>{{ f.weight.toFixed(2) }}</td>
                            <td>{{ f.score != null ? (f.score * f.weight).toFixed(3) : '-' }}</td>
                          </tr>
                        </tbody>
                      </table>
                      <ol class="eval-steps" v-if="v.detail.steps">
                        <li v-for="(s, si) in v.detail.steps" :key="si">{{ s }}</li>
                      </ol>
                      <p class="eval-warning" v-if="v.detail.warning">{{ v.detail.warning }}</p>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </section>
    </section>
  </main>
</template>

<script>
export default {
  name: 'market-report',
  data: function () {
    return {
      report: null,
      reportDates: [],
      valuations: [],
      queryDate: '',
      expandedSymbol: '',
      loading: false,
      error: '',
      buffett: null,
      buffettLoading: false,
      buffettError: '',
      buffettJustRefreshed: false,
      showBuffettDetail: false,
      fxSummary: null,
      fxError: '',
      activeFxKey: 'usd_cny'
    }
  },
  computed: {
    modelName: function () {
      var first = this.valuations.find(function (v) { return v.detail && v.detail.model })
      return first ? first.detail.model : ''
    },
    activeFxComparisons: function () {
      if (!this.fxSummary) return []
      var sets = this.fxSummary.comparison_sets || {}
      if (sets[this.activeFxKey]) return sets[this.activeFxKey]
      return this.activeFxKey === 'usd_cny' ? (this.fxSummary.comparisons || []) : []
    },
    activeFxLabel: function () {
      return {
        usd_cny: '美元 / 人民币',
        cny_jpy: '人民币 / 日元',
        usd_jpy: '美元 / 日元'
      }[this.activeFxKey] || ''
    },
    activeFxNote: function () {
      if (!this.fxSummary) return ''
      var notes = this.fxSummary.baseline_notes || {}
      return notes[this.activeFxKey] || this.fxSummary.baseline_note || ''
    }
  },
  mounted: function () {
    this.loadReportDates()
    this.loadReport()
    this.loadValuations()
    this.loadBuffett(false)
    this.loadFxSummary()
  },
  methods: {
    loadReportDates: function () {
      var self = this
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/report/dates/?limit=30'
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.reportDates = res.dates || []
        }
      }).catch(function () {
        self.reportDates = []
      })
    },

    loadLatestReport: function () {
      this.queryDate = ''
      this.loadReport()
    },

    loadBuffett: function (refresh) {
      var self = this
      self.buffettLoading = true
      self.buffettError = ''
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/buffett/' + (refresh ? '?refresh=1' : '')
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.buffett = res.latest
          self.buffettJustRefreshed = !!res.refreshed
          if (refresh && !res.refreshed) {
            self.buffettError = '10分钟内已有快照，展示最近一次采集结果'
          }
        }
        self.buffettLoading = false
      }).catch(function (err) {
        self.buffettLoading = false
        try {
          var res = JSON.parse(err.bodyText)
          self.buffettError = res.message || '证券化率加载失败'
          if (res.latest) self.buffett = res.latest
        } catch (e) {
          self.buffettError = '证券化率加载失败'
        }
      })
    },

    refreshBuffett: function () {
      this.loadBuffett(true)
    },

    loadReport: function () {
      var self = this
      self.loading = true
      self.error = ''
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/report/' +
        (this.queryDate ? this.queryDate + '/' : 'latest/')
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.report = res.report
        } else {
          self.report = null
          self.error = res.message || '加载失败'
        }
        self.loading = false
      }).catch(function (err) {
        self.report = null
        self.error = err.status === 404 ? '报告不存在' : ('请求失败: ' + err.statusText)
        self.loading = false
      })
    },

    loadValuations: function () {
      var self = this
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/valuations/'
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.valuations = res.valuations
        }
      }).catch(function (err) {
        console.error('加载估值失败:', err)
      })
    },

    loadFxSummary: function () {
      var self = this
      self.fxError = ''
      // schema 参数用于隔离旧版接口的浏览器/CDN缓存；v2包含三组历史对比。
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/fx-summary/?schema=2'
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.fxSummary = res.summary
        }
      }).catch(function () {
        self.fxSummary = null
        self.fxError = '重点汇率加载失败'
      })
    },

    signedPct: function (value) {
      if (value == null) return '-'
      return (value > 0 ? '+' : '') + Number(value).toFixed(2) + '%'
    },

    fxDirectionClass: function (item) {
      if (!item || item.change_pct == null) return ''
      if (item.direction && item.direction.indexOf('升值') !== -1) return 'currency-up'
      if (item.direction && item.direction.indexOf('贬值') !== -1) return 'currency-down'
      return ''
    },

    fmt: function (val) {
      return val != null ? Number(val).toFixed(2) : '-'
    },

    toggleExpand: function (symbol) {
      this.expandedSymbol = this.expandedSymbol === symbol ? '' : symbol
    },

    compositeOf: function (v) {
      if (!v.detail || v.detail.composite == null) return '-'
      var c = v.detail.composite
      return (c > 0 ? '+' : '') + c.toFixed(2)
    },

    compositeClass: function (v) {
      if (!v.detail || v.detail.composite == null) return ''
      return Math.abs(v.detail.composite) >= (v.detail.threshold || 1.0) ? 'composite-active' : ''
    },

    statusLabel: function (status) {
      return { generated: '已生成', sent: '已发送', send_failed: '发送失败' }[status] || status
    },

    categoryLabel: function (category) {
      return {
        commodity_future: '商品期货',
        commodity_stock: '商品股票',
        fx: '汇率',
        index: '指数'
      }[category] || category
    },

    verdictLabel: function (verdict) {
      return { undervalued: '低估', overvalued: '高估', neutral: '中性' }[verdict] || verdict
    }
  }
}
</script>

<style scoped>
.market-report {
  min-height: 100vh;
  background: #f5f7fb;
  color: #1f2933;
}

.report-hero {
  padding: 48px 20px 44px;
  text-align: center;
  background: #fff;
  border-bottom: 1px solid #e4e9f0;
}

.report-hero p {
  margin: 0 0 8px;
  color: #52709a;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0;
}

.report-hero h1 {
  margin: 0;
  color: #111827;
  font-size: 36px;
  line-height: 1.15;
}

.report-hero span {
  display: block;
  margin-top: 12px;
  color: #667085;
  font-size: 16px;
}

.report-shell {
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
  padding: 28px 0 72px;
}

.control-panel,
.buffett-panel,
.fx-panel,
.report-card,
.valuation-panel {
  border: 1px solid #dde5ef;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

.control-panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(190px, auto);
  gap: 18px;
  align-items: end;
  padding: 18px;
  margin-bottom: 18px;
}

.control-main {
  display: grid;
  grid-template-columns: minmax(180px, 260px) minmax(180px, 280px);
  gap: 14px;
}

.control-main label span {
  display: block;
  margin-bottom: 7px;
  color: #667085;
  font-size: 12px;
  font-weight: 700;
}

.control-main input,
.control-main select {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #cfd8e3;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fff;
  color: #111827;
  font-size: 15px;
}

.control-actions {
  display: flex;
  gap: 10px;
}

button {
  border: 1px solid #1f6feb;
  border-radius: 8px;
  padding: 10px 16px;
  background: #1f6feb;
  color: #fff;
  font-size: 15px;
  cursor: pointer;
}

button.secondary {
  border-color: #cfd8e3;
  background: #fff;
  color: #344054;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

.report-status-card {
  display: grid;
  gap: 3px;
  justify-items: end;
  color: #667085;
  font-size: 12px;
}

.report-status-card strong {
  color: #111827;
  font-size: 18px;
}

.report-status-card em {
  font-style: normal;
  font-weight: 700;
}

.status-sent { color: #16833a; }
.status-send_failed { color: #c62828; }
.status-generated { color: #b26a00; }

.state-message {
  padding: 14px 0;
  color: #667085;
}

.error-msg { color: #c62828; }

.panel-heading {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: center;
  padding: 18px 20px 0;
}

.panel-heading p {
  margin: 0 0 5px;
  color: #667085;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0;
}

.panel-heading h2 {
  margin: 0;
  color: #111827;
  font-size: 22px;
}

.buffett-panel,
.fx-panel,
.report-card,
.valuation-panel {
  margin-top: 18px;
}

.buffett-grid {
  display: grid;
  grid-template-columns: 1.2fr repeat(3, 1fr);
  gap: 12px;
  padding: 18px 20px;
}

.buffett-grid > div {
  min-height: 82px;
  border: 1px solid #e5eaf0;
  border-radius: 8px;
  padding: 14px;
  background: #f8fafc;
}

.buffett-grid span {
  display: block;
  color: #667085;
  font-size: 13px;
}

.buffett-grid strong {
  display: block;
  margin-top: 10px;
  color: #111827;
  font-size: 20px;
}

.ratio-block strong {
  margin: 0;
  font-size: 42px;
  line-height: 1;
}

.ratio-block.verdict-undervalued strong { color: #16833a; }
.ratio-block.verdict-overvalued strong { color: #c62828; }
.ratio-block.verdict-neutral strong { color: #667085; }

.buffett-footer {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 0 20px 18px;
  color: #667085;
  font-size: 13px;
}

.buffett-footer a {
  color: #1f6feb;
  cursor: pointer;
}

.buffett-note {
  padding: 0 20px 14px;
  color: #b26a00;
  font-size: 13px;
}

.fx-as-of {
  color: #667085;
  font-size: 13px;
}

.fx-rate-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding: 18px 20px 12px;
}

.fx-rate-card {
  display: flex;
  width: 100%;
  min-height: 122px;
  flex-direction: column;
  border: 1px solid #e5eaf0;
  border-radius: 8px;
  padding: 16px;
  background: #f8fafc;
  color: inherit;
  font-family: inherit;
  text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.fx-rate-card:hover {
  border-color: #9fbce6;
}

.fx-rate-card:focus-visible {
  outline: 3px solid rgba(31, 111, 235, 0.22);
  outline-offset: 2px;
}

.fx-rate-card.active {
  border-color: #bcd0ee;
  background: #f3f7fd;
  box-shadow: inset 0 0 0 1px #bcd0ee;
}

.fx-rate-card span,
.fx-change-card span {
  color: #667085;
  font-size: 13px;
  font-weight: 700;
}

.fx-rate-card strong {
  margin-top: 8px;
  color: #111827;
  font-size: 30px;
  line-height: 1.1;
}

.fx-rate-card small {
  margin-top: 8px;
  color: #344054;
  font-size: 13px;
}

.fx-rate-card em,
.fx-change-card em {
  margin-top: auto;
  padding-top: 10px;
  color: #98a2b3;
  font-size: 11px;
  font-style: normal;
}

.fx-comparison {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  padding: 0 20px 12px;
}

.fx-switch-hint {
  margin: 0;
  padding: 0 20px 10px;
  color: #52709a;
  font-size: 12px;
  font-weight: 700;
}

.fx-change-card {
  display: flex;
  min-height: 86px;
  flex-direction: column;
  border: 1px solid #e5eaf0;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
}

.fx-change-card strong {
  margin-top: 5px;
  color: #344054;
  font-size: 20px;
}

.fx-change-card small {
  margin-top: 3px;
  color: #667085;
  font-size: 12px;
}

.fx-change-card.currency-up strong,
.fx-change-card.currency-up small { color: #16833a; }

.fx-change-card.currency-down strong,
.fx-change-card.currency-down small { color: #c62828; }

.fx-note {
  margin: 0;
  padding: 0 20px 18px;
  color: #667085;
  font-size: 12px;
}

.report-body {
  margin: 18px 20px 20px;
  border: 1px solid #e5eaf0;
  border-radius: 8px;
  padding: 18px;
  background: #fbfcfe;
  color: #2d3748;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
  line-height: 1.75;
}

.model-name {
  color: #667085;
  font-size: 13px;
}

.model-hint {
  margin: 8px 20px 14px;
  color: #667085;
  font-size: 13px;
}

.table-wrapper {
  overflow-x: auto;
  padding: 0 20px 20px;
}

.valuation-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.valuation-table th,
.valuation-table td {
  border-bottom: 1px solid #e5eaf0;
  padding: 10px 8px;
  text-align: left;
  white-space: nowrap;
}

.valuation-table th {
  color: #667085;
  font-weight: 700;
}

.valuation-row {
  cursor: pointer;
}

.valuation-row:hover {
  background: #f4f7fb;
}

.ticker-cell {
  color: #111827;
  font-family: monospace;
  font-weight: 700;
}

.verdict-undervalued { color: #16833a; font-weight: 700; }
.verdict-overvalued { color: #c62828; font-weight: 700; }
.verdict-neutral { color: #667085; font-weight: 700; }

.composite-active {
  color: #111827;
  font-weight: 700;
}

.confidence {
  margin-left: 4px;
  color: #667085;
  font-size: 11px;
}

.detail-row td {
  background: #fbfcfe;
  white-space: normal;
}

.eval-process h4 {
  margin: 0 0 10px;
  font-size: 14px;
}

.factor-table {
  width: auto;
  min-width: 60%;
  border-collapse: collapse;
  font-size: 12px;
  margin-bottom: 12px;
}

.factor-table th,
.factor-table td {
  border: 1px solid #dce5ef;
  padding: 6px 10px;
  text-align: right;
}

.factor-table th:first-child,
.factor-table td:first-child {
  text-align: left;
}

.eval-steps {
  margin: 0;
  padding: 0 20px 18px 38px;
  color: #52616f;
  font-size: 13px;
  line-height: 1.8;
}

.eval-warning {
  color: #c62828;
  font-size: 12px;
  margin: 8px 0 0;
}

@media (max-width: 900px) {
  .report-shell {
    width: min(100% - 28px, 720px);
  }

  .control-panel {
    grid-template-columns: 1fr;
    align-items: stretch;
  }

  .control-main {
    grid-template-columns: 1fr;
  }

  .report-status-card {
    justify-items: start;
  }

  .buffett-grid {
    grid-template-columns: 1fr 1fr;
  }

  .fx-comparison {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 560px) {
  .report-hero h1 {
    font-size: 30px;
  }

  .control-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .buffett-grid {
    grid-template-columns: 1fr;
  }

  .fx-rate-grid,
  .fx-comparison {
    grid-template-columns: 1fr;
  }

  .panel-heading {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
